import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { components } from 'react-select-v3';
import AsyncSelect from 'react-select-v3/async';
import { Icon } from '@ontruck/orc';
import * as http from 'utils/http';
import { carriersAndDriversIncreasingAlphabetical as sortCarriersDrivers } from 'utils/sort';
import config from 'config';
import { injectIntl } from 'react-intl';
import serialize from 'utils/serialize';
import debouncePromise from 'debounce-promise';

import { StyledFilter, reactSelectStyles } from './styled';

const t = {
  carrierDriver: 'Car / Dri - ',
  carrier: 'Carr - ',
  driver: 'Dri - ',
};

const markCarrierAndDrivers = (driverCarrier, _, array) => {
  // is driver
  if (driverCarrier.carrier_id) {
    return {
      ...driverCarrier,
      isDriver: true,
    };
  }

  const carrier = driverCarrier;
  const { drivers = [] } = carrier;

  if (drivers.length === 1) {
    const driverId = drivers[0];
    const driver = array.find((d) => d.id === driverId);
    if (!!driver && driver.contact_name === carrier.name) {
      return {
        ...carrier,
        isCarrierDriver: true,
      };
    }
    return {
      ...carrier,
      isCarrier: true,
    };
  }

  // Is "normal" carrier
  return {
    ...carrier,
    isCarrier: true,
  };
};

const filterDriversBelongsToCarrierDriver = (driverCarrier, _, array) => {
  // is carrier
  if (driverCarrier.isCarrier || driverCarrier.isCarrierDriver) {
    return true;
  }

  // is driver
  const carrier = array.find((dc) => dc.id === driverCarrier.carrier_id);
  if (!!carrier && carrier.isCarrierDriver) {
    return false;
  }

  return true;
};

const mapCarrierDriver = (carrierDriver) => {
  // This is carrier/driver
  if (carrierDriver.isCarrierDriver) {
    return {
      ...carrierDriver,
      label: `${t.carrierDriver}${carrierDriver.name}`,
      value: carrierDriver.id,
    };
  }

  // This is carrier
  if (carrierDriver.isCarrier) {
    return {
      ...carrierDriver,
      label: `${t.carrier}${carrierDriver.name}`,
      value: carrierDriver.id,
    };
  }

  // This is driver
  if (carrierDriver.isDriver) {
    return {
      ...carrierDriver,
      label: `${t.driver}${carrierDriver.contact_name}`,
      value: carrierDriver.id,
    };
  }
};

class CarrierDriverFilter extends Component {
  static filterName = 'CarrierDriverFilter';

  static propTypes = {
    onChange: PropTypes.func,
    carrier: PropTypes.any,
    driver: PropTypes.any,
    carrierDriver: PropTypes.any,
    intl: PropTypes.any,
    clearable: PropTypes.bool,
    disabled: PropTypes.bool,
    invalid: PropTypes.bool,
    stylingProps: PropTypes.object,
    withBorder: PropTypes.bool,
    selectStyles: PropTypes.object,
  };

  static defaultPropTypes = {
    clearable: true,
    disabled: false,
    invalid: false,
    withBorder: false,
  };

  constructor(props) {
    super(props);
    this.handleChange = this.handleChange.bind(this);
    this.getCarriersAndDrivers = debouncePromise(this.getCarriersAndDrivers.bind(this), 500, {
      leading: true,
    });
  }

  handleChange(value) {
    if (!this.props.onChange) {
      return;
    }

    const { onChange } = this.props;

    if (!value) {
      onChange(null);
      return;
    }

    const { label } = value;

    if ((label || '').startsWith(t.driver)) {
      onChange({ value, type: 'driver' });
      return;
    }

    if ((label || '').startsWith(t.carrier) || (label || '').startsWith(t.carrierDriver)) {
      onChange({ value, type: 'carrier' });
      return;
    }
  }

  async getCarriersAndDrivers(input) {
    if (!input || input.length < 3) {
      return Promise.resolve([{}]);
    }

    const driverParams = {
      search: encodeURIComponent(input),
      page_size: 0,
      ordering: 'contact_name',
      fields: ['id', 'contact_name', 'carrier_id'].join(','),
    };

    const carrierParams = {
      search: encodeURIComponent(input),
      page_size: 0,
      fields: ['id', 'name', 'drivers'].join(','),
    };

    const [drivers, carriers] = await Promise.all([
      http.get(`${config.API_URL}mercury/drivers/?${serialize(driverParams)}`),
      http.get(`${config.API_URL}mercury/carriers/?${serialize(carrierParams)}`),
    ]);

    const carriersDrivers = [...carriers, ...drivers]
      .map(markCarrierAndDrivers)
      .filter(filterDriversBelongsToCarrierDriver)
      .map(mapCarrierDriver)
      .sort(sortCarriersDrivers);

    return carriersDrivers;
  }

  DropdownIndicator = (props) => {
    return (
      <components.DropdownIndicator {...props}>
        <Icon glyph="search" size="1.5rem" />
      </components.DropdownIndicator>
    );
  };

  render() {
    const {
      intl,
      disabled,
      invalid,
      carrier,
      driver,
      stylingProps,
      placeholder,
      withBorder,
      selectStyles,
    } = this.props;

    const value = carrier ? carrier : driver ? driver : undefined;

    const t = intl.messages.components.ShipmentFilters;

    return (
      <StyledFilter {...stylingProps} withBorder={withBorder}>
        <AsyncSelect
          isClearable
          components={{ DropdownIndicator: this.DropdownIndicator }}
          placeholder={placeholder || t.carrier_driver_filter_placeholder}
          disabled={disabled}
          invalid={invalid}
          value={value}
          onChange={this.handleChange}
          loadOptions={this.getCarriersAndDrivers}
          styles={reactSelectStyles}
          {...selectStyles}
        />
      </StyledFilter>
    );
  }
}

export default injectIntl(CarrierDriverFilter);
